import { useCallback, useEffect, useState } from 'react';
import cn from 'classnames';

import CarouselArrow from 'components/icons/CarouselArrow';
import CarouselControlsProps from 'components/common/CarouselControls/CarouselControls.types';
import useMartyContext from 'hooks/useMartyContext';
import css from 'components/common/CarouselControls/CarouselControls.scss';

const CarouselControls = (props: CarouselControlsProps) => {
  const { emblaApi, isAutoHidden = true } = props;
  const { testId } = useMartyContext();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  const scrollTo = useCallback((index: number) => emblaApi && emblaApi.scrollTo(index), [emblaApi]);

  const onInit = useCallback(() => {
    if (!emblaApi) {
      return;
    }
    setScrollSnaps(emblaApi.scrollSnapList());
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) {
      return;
    }
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }
    onInit();
    onSelect();
    emblaApi.on('reInit', onInit);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('select', onSelect);
  }, [emblaApi, onInit, onSelect]);

  if (isAutoHidden && scrollSnaps.length === 1) {
    // do not show controls if theres only 1 page
    return null;
  }

  return (
    <div className={css.dots} data-test-id={testId('carouselControls')}>
      <button
        data-test-id={testId('carouselControlsLeftArrow')}
        type="button"
        className={css.leftArrowBtn}
        onClick={scrollPrev}
        disabled={prevBtnDisabled}
      >
        <CarouselArrow className={cn(css.arrowLeft, { [css.arrowDisabled]: prevBtnDisabled })} />
      </button>
      <div className={css.dotsContainer}>
        {scrollSnaps.map((_, index) => (
          <button
            type="button"
            key={index}
            onClick={() => scrollTo(index)}
            className={cn(css.dot, { [css.dotSelected]: index === selectedIndex })}
            data-test-id={testId('carouselPageDot')}
          />
        ))}
      </div>
      <button
        data-test-id={testId('carouselControlsRightArrow')}
        type="button"
        className={css.rightArrowBtn}
        onClick={scrollNext}
        disabled={nextBtnDisabled}
      >
        <CarouselArrow className={cn({ [css.arrowDisabled]: nextBtnDisabled })} />
      </button>
    </div>
  );
};

export default CarouselControls;
